// Link list component with optimistic updates, bulk actions, clone, edit, QR, tags, filters, campaign badges
import React, { useState, useMemo, useRef, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  FiBarChart2, FiCopy, FiCheck, FiToggleLeft, FiToggleRight,
  FiTrash2, FiExternalLink, FiLock, FiClock, FiEdit2,
  FiGrid, FiSmartphone, FiLayers, FiFilter, FiCalendar, FiX,
} from "react-icons/fi";
import { api } from "../utils/api";
import { useToast } from "./ToastProvider";
import ConfirmDialog from "./ConfirmDialog";
import QRCodeModal from "./QRCodeModal";
import EditLinkModal from "./EditLinkModal";
import BulkEditModal from "./BulkEditModal";
import { formatDate, formatDateTimeWithTz } from "../utils/timezone";
import { getFaviconUrlSync, prewarmFaviconCache } from "../utils/faviconResolver";
import "../styles/LinkList.css";
import "../styles/Campaigns.css";

const TAG_COLORS = [
  "#2383e2", "#0f7b6c", "#cb912f", "#d44040", "#7c3aed",
  "#0891b2", "#be185d", "#65a30d", "#6366f1", "#f59e0b",
];

function getTagColor(tag) {
  let hash = 0;
  for (let i = 0; i < tag.length; i++) hash = tag.charCodeAt(i) + ((hash << 5) - hash);
  return TAG_COLORS[Math.abs(hash) % TAG_COLORS.length];
}

export default function LinkList({ links, setLinks, setTotal, refreshSummary, refreshLinks, statusFilter, onStatusFilter, linksLoading }) {
  const navigate = useNavigate();
  const { addToast } = useToast();
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [togglingId, setTogglingId] = useState(null);
  const [copiedId, setCopiedId] = useState(null);
  const [cloningId, setCloningId] = useState(null);
  const [qrLink, setQrLink] = useState(null);
  const [editLink, setEditLink] = useState(null);
  const [showBulkEdit, setShowBulkEdit] = useState(false);

  // Bulk selection
  const [selected, setSelected] = useState(new Set());
  const [bulkLoading, setBulkLoading] = useState(false);

  // Filter state
  const [showDateFilter, setShowDateFilter] = useState(false);
  const [showAdvFilter, setShowAdvFilter] = useState(false);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [filterTag, setFilterTag] = useState("");
  const [filterLinkType, setFilterLinkType] = useState("all");
  const [filterExpiry, setFilterExpiry] = useState("all");
  const [filterCampaign, setFilterCampaign] = useState("");

  // Campaign lookup for badges
  const [campaignMap, setCampaignMap] = useState({});

  const dateFilterRef = useRef(null);
  const advFilterRef = useRef(null);

  // Fetch campaigns for badge display
  useEffect(() => {
    api.getCampaigns()
      .then((res) => {
        const map = {};
        (res.campaigns || []).forEach((c) => { map[c.$id] = c; });
        setCampaignMap(map);
      })
      .catch(() => {}); // Silently ignore — badges are non-critical
  }, []);

  // Prewarm favicon cache for all links
  useEffect(() => {
    if (links.length > 0) {
      const urls = links.map((l) => l.original_url).filter(Boolean);
      prewarmFaviconCache(urls).catch(() => {}); // Silently ignore
    }
  }, [links]);

  // Close dropdowns on outside click
  useEffect(() => {
    function handleClick(e) {
      if (dateFilterRef.current && !dateFilterRef.current.contains(e.target)) setShowDateFilter(false);
      if (advFilterRef.current && !advFilterRef.current.contains(e.target)) setShowAdvFilter(false);
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  // Gather all unique tags from links
  const allTags = useMemo(() => {
    const s = new Set();
    links.forEach((l) => {
      if (l.tags) l.tags.split(",").filter(Boolean).forEach((t) => s.add(t.trim()));
    });
    return [...s].sort();
  }, [links]);

  // Apply client-side filters
  const filteredLinks = useMemo(() => {
    return links.filter((link) => {
      // Date filter
      if (dateFrom) {
        const created = new Date(link.created_at);
        if (created < new Date(dateFrom)) return false;
      }
      if (dateTo) {
        const created = new Date(link.created_at);
        const end = new Date(dateTo);
        end.setHours(23, 59, 59, 999);
        if (created > end) return false;
      }
      // Tag filter
      if (filterTag) {
        const linkTags = link.tags ? link.tags.split(",").map((t) => t.trim()) : [];
        if (!linkTags.includes(filterTag)) return false;
      }
      // Campaign filter
      if (filterCampaign && link.campaign_id !== filterCampaign) return false;
      // Link type filter
      if (filterLinkType === "custom") {
        // Custom links typically have short codes that aren't purely random (contain letters with meaning, slashes, etc.)
        // Heuristic: auto-generated codes are exactly 7 chars alphanumeric
        if (/^[a-zA-Z0-9]{7}$/.test(link.short_code)) return false;
      } else if (filterLinkType === "auto") {
        if (!/^[a-zA-Z0-9]{7}$/.test(link.short_code)) return false;
      }
      // Expiry filter
      if (filterExpiry === "expired") {
        if (!link.expires_at || new Date(link.expires_at) > new Date()) return false;
      } else if (filterExpiry === "expiring") {
        if (!link.expires_at) return false;
        if (new Date(link.expires_at) <= new Date()) return false;
      } else if (filterExpiry === "none") {
        if (link.expires_at) return false;
      }
      // Status filter (client-side for scheduled/expired since server can't compute date-based status)
      if (statusFilter === "scheduled" && link.status !== "scheduled") return false;
      if (statusFilter === "expired" && link.status !== "expired") return false;
      return true;
    });
  }, [links, dateFrom, dateTo, filterTag, filterCampaign, filterLinkType, filterExpiry, statusFilter]);

  const hasActiveFilters = dateFrom || dateTo || filterTag || filterCampaign || filterLinkType !== "all" || filterExpiry !== "all";

  const clearAllFilters = () => {
    setDateFrom("");
    setDateTo("");
    setFilterTag("");
    setFilterLinkType("all");
    setFilterExpiry("all");
    setFilterCampaign("");
  };

  const toggleSelect = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selected.size === filteredLinks.length) {
      setSelected(new Set());
    } else {
      setSelected(new Set(filteredLinks.map((l) => l.id)));
    }
  };

  // --- Optimistic: Bulk actions ---
  const handleBulkAction = async (action) => {
    if (selected.size === 0) return;
    const selectedIds = [...selected];
    const snapshot = [...links];

    setBulkLoading(true);

    if (action === "activate") {
      setLinks((prev) => prev.map((l) => selectedIds.includes(l.id) ? { ...l, is_active: true } : l));
    } else if (action === "deactivate") {
      setLinks((prev) => prev.map((l) => selectedIds.includes(l.id) ? { ...l, is_active: false } : l));
    } else if (action === "delete") {
      setLinks((prev) => prev.filter((l) => !selectedIds.includes(l.id)));
      setTotal((t) => t - selectedIds.length);
    }

    setSelected(new Set());

    try {
      await api.bulkAction(action, selectedIds);
      const label = action === "delete" ? "deleted" : action === "activate" ? "activated" : "deactivated";
      addToast(`${selectedIds.length} link(s) ${label}`, "success");
      if (action === "delete") refreshSummary();
    } catch (err) {
      setLinks(snapshot);
      if (action === "delete") setTotal((t) => t + selectedIds.length);
      addToast("Bulk action failed: " + err.message, "error");
    } finally {
      setBulkLoading(false);
    }
  };

  const copyUrl = (link) => {
    navigator.clipboard.writeText(link.short_url);
    setCopiedId(link.id);
    addToast("Link copied to clipboard!", "success");
    setTimeout(() => setCopiedId(null), 1500);
  };

  const toggleActive = async (link) => {
    const newState = !link.is_active;
    setLinks((prev) => prev.map((l) => l.id === link.id ? { ...l, is_active: newState } : l));
    setTogglingId(link.id);
    try {
      await api.updateLink(link.id, { is_active: newState });
      addToast(newState ? "Link activated" : "Link deactivated", newState ? "success" : "warning");
    } catch (err) {
      setLinks((prev) => prev.map((l) => l.id === link.id ? { ...l, is_active: link.is_active } : l));
      addToast("Failed to toggle link: " + err.message, "error");
    } finally {
      setTogglingId(null);
    }
  };

  const cloneLink = async (link) => {
    setCloningId(link.id);
    try {
      const cloned = await api.cloneLink(link.id);
      setLinks((prev) => {
        const idx = prev.findIndex((l) => l.id === link.id);
        const newLink = {
          id: cloned.id, short_code: cloned.short_code, short_url: cloned.short_url,
          original_url: cloned.original_url, title: cloned.title || "", click_count: 0,
          is_active: true, created_at: cloned.created_at, expires_at: cloned.expires_at || null,
          has_password: cloned.has_password || false, tags: cloned.tags || "", mobile_url: cloned.mobile_url || "",
        };
        const next = [...prev];
        next.splice(idx + 1, 0, newLink);
        return next;
      });
      setTotal((t) => t + 1);
      addToast("Link cloned successfully!", "success");
      refreshSummary();
    } catch (err) {
      addToast("Failed to clone link: " + err.message, "error");
    } finally {
      setCloningId(null);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    const snapshot = [...links];
    const targetId = deleteTarget.id;
    setLinks((prev) => prev.filter((l) => l.id !== targetId));
    setTotal((t) => t - 1);
    setDeleteTarget(null);
    try {
      await api.deleteLink(targetId);
      addToast("Link deleted successfully", "success");
      refreshSummary();
    } catch (err) {
      setLinks(snapshot);
      setTotal((t) => t + 1);
      addToast("Failed to delete link: " + err.message, "error");
    }
  };

  const handleBulkUpdate = async (updates) => {
    if (selected.size === 0) {
      addToast("Select at least one link to bulk edit.", "warning");
      return;
    }

    const selectedIds = [...selected];
    setBulkLoading(true);
    try {
      const result = await api.bulkAction("update", selectedIds, updates);
      const failed = Array.isArray(result.errors) ? result.errors.length : 0;
      const processed = result.processed || 0;

      if (processed > 0 && failed === 0) {
        addToast(`Updated ${processed} link(s).`, "success");
      } else if (processed > 0 && failed > 0) {
        addToast(`Updated ${processed} link(s), ${failed} failed.`, "warning");
      } else {
        addToast("No links were updated.", "warning");
      }

      setShowBulkEdit(false);
      setSelected(new Set());
      await refreshLinks();
      refreshSummary();
    } catch (err) {
      addToast("Bulk edit failed: " + err.message, "error");
    } finally {
      setBulkLoading(false);
    }
  };

  const handleEditUpdated = (updatedLink) => {
    setLinks((prev) => prev.map((l) =>
      l.id === updatedLink.id ? {
        ...l,
        short_code: updatedLink.short_code,
        short_url: updatedLink.short_url,
        original_url: updatedLink.original_url,
        title: updatedLink.title,
        expires_at: updatedLink.expires_at,
        tags: updatedLink.tags || "",
        mobile_url: updatedLink.mobile_url || "",
        has_password: updatedLink.has_password,
        is_active: updatedLink.is_active,
        redirect_type: updatedLink.redirect_type || "302",
        redirect_time: updatedLink.redirect_time != null ? updatedLink.redirect_time : 10,
      } : l
    ));
    setEditLink(null);
  };

  return (
    <>
      {/* Toolbar: status tabs + filter buttons */}
      <div className="link-toolbar">
        <div className="filter-tabs">
          {[
            { key: "", label: "All" },
            { key: "active", label: "Active" },
            { key: "scheduled", label: "Scheduled" },
            { key: "expired", label: "Expired" },
            { key: "inactive", label: "Inactive" },
          ].map(({ key, label }) => (
            <button
              key={key}
              className={`filter-tab ${statusFilter === key ? "active" : ""}`}
              onClick={() => onStatusFilter(key)}
            >
              {label}
            </button>
          ))}
        </div>
        <div className="filter-actions">
          {/* Date filter */}
          <div className="filter-dropdown-wrapper" ref={dateFilterRef}>
            <button
              className={`filter-btn ${dateFrom || dateTo ? "filter-btn-active" : ""}`}
              onClick={() => { setShowDateFilter(!showDateFilter); setShowAdvFilter(false); }}
            >
              <FiCalendar size={13} />
              <span>Date</span>
            </button>
            {showDateFilter && (
              <div className="filter-dropdown">
                <div className="filter-dropdown-title">Filter by creation date</div>
                <div className="filter-date-row">
                  <div className="filter-date-field">
                    <label>From</label>
                    <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
                  </div>
                  <div className="filter-date-field">
                    <label>To</label>
                    <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
                  </div>
                </div>
                {(dateFrom || dateTo) && (
                  <button className="filter-clear-btn" onClick={() => { setDateFrom(""); setDateTo(""); }}>
                    Clear dates
                  </button>
                )}
              </div>
            )}
          </div>

          {/* Advanced filters */}
          <div className="filter-dropdown-wrapper" ref={advFilterRef}>
            <button
              className={`filter-btn ${(filterTag || filterCampaign || filterLinkType !== "all" || filterExpiry !== "all") ? "filter-btn-active" : ""}`}
              onClick={() => { setShowAdvFilter(!showAdvFilter); setShowDateFilter(false); }}
            >
              <FiFilter size={13} />
              <span>Filter</span>
            </button>
            {showAdvFilter && (
              <div className="filter-dropdown filter-dropdown-wide">
                {/* Tag filter */}
                <div className="filter-section">
                  <div className="filter-dropdown-title">Tags</div>
                  <select value={filterTag} onChange={(e) => setFilterTag(e.target.value)} className="filter-select">
                    <option value="">All tags</option>
                    {allTags.map((t) => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>

                {/* Campaign filter */}
                <div className="filter-section">
                  <div className="filter-dropdown-title">Campaign</div>
                  <select value={filterCampaign} onChange={(e) => setFilterCampaign(e.target.value)} className="filter-select">
                    <option value="">All campaigns</option>
                    {Object.values(campaignMap).map((c) => <option key={c.$id} value={c.$id}>{c.name}</option>)}
                  </select>
                </div>

                {/* Link type */}
                <div className="filter-section">
                  <div className="filter-dropdown-title">Link type</div>
                  <select value={filterLinkType} onChange={(e) => setFilterLinkType(e.target.value)} className="filter-select">
                    <option value="all">All links</option>
                    <option value="custom">Custom link</option>
                    <option value="auto">Auto-generated</option>
                  </select>
                </div>

                {/* Expiration */}
                <div className="filter-section">
                  <div className="filter-dropdown-title">Expiration</div>
                  <div className="filter-radio-group">
                    {[
                      { key: "all", label: "All" },
                      { key: "expired", label: "Expired" },
                      { key: "expiring", label: "Expiring" },
                      { key: "none", label: "No expiration" },
                    ].map(({ key, label }) => (
                      <label key={key} className={`filter-radio ${filterExpiry === key ? "active" : ""}`}>
                        <span className={`filter-radio-dot ${filterExpiry === key ? "active" : ""}`} />
                        <input
                          type="radio"
                          name="expiry-filter"
                          value={key}
                          checked={filterExpiry === key}
                          onChange={() => setFilterExpiry(key)}
                        />
                        {label}
                      </label>
                    ))}
                  </div>
                </div>

                {(filterTag || filterCampaign || filterLinkType !== "all" || filterExpiry !== "all") && (
                  <button className="filter-clear-btn" onClick={() => { setFilterTag(""); setFilterCampaign(""); setFilterLinkType("all"); setFilterExpiry("all"); }}>
                    Clear filters
                  </button>
                )}
              </div>
            )}
          </div>

          {hasActiveFilters && (
            <button className="filter-clear-all" onClick={clearAllFilters}>
              <FiX size={12} /> Clear all
            </button>
          )}
        </div>

        {/* Bulk actions bar */}
        {selected.size > 0 && (
          <div className="bulk-bar">
            <span className="bulk-count">{selected.size} selected</span>
            <button className="btn-sm" onClick={() => handleBulkAction("activate")} disabled={bulkLoading}>
              {bulkLoading ? <span className="spinner-sm dark" /> : "Activate"}
            </button>
            <button className="btn-sm" onClick={() => handleBulkAction("deactivate")} disabled={bulkLoading}>
              Deactivate
            </button>
            <button className="btn-sm btn-sm-danger" onClick={() => handleBulkAction("delete")} disabled={bulkLoading}>
              Delete
            </button>
            <button className="btn-sm" onClick={() => setShowBulkEdit(true)} disabled={bulkLoading}>
              Bulk Edit
            </button>
            <button className="btn-sm" onClick={() => setSelected(new Set())}>
              Clear
            </button>
          </div>
        )}
      </div>

      {linksLoading ? (
        <div className="links-loading">
          <div className="spinner" />
        </div>
      ) : filteredLinks.length === 0 ? (
        <div className="link-list-empty">
          <p>{hasActiveFilters ? "No links match the current filters." : "No links found."}</p>
        </div>
      ) : (
        <div className="link-list">
          <div className="link-list-select-all">
            <div className="link-col-check">
              <label className="checkbox-wrapper">
                <input
                  type="checkbox"
                  checked={selected.size === filteredLinks.length && filteredLinks.length > 0}
                  onChange={toggleSelectAll}
                />
              </label>
            </div>
            <span className="select-all-label">
              {selected.size > 0
                ? `${selected.size} of ${filteredLinks.length} selected`
                : `${filteredLinks.length} link${filteredLinks.length !== 1 ? "s" : ""}${hasActiveFilters ? " (filtered)" : ""}`
              }
            </span>
          </div>

          {filteredLinks.map((link) => {
            const linkTags = link.tags ? link.tags.split(",").filter(Boolean) : [];
            const faviconUrl = getFaviconUrlSync(link.original_url);
            return (
              <div key={link.id} className={`link-card ${!link.is_active ? "inactive" : ""} ${selected.has(link.id) ? "selected" : ""}`}>
                <div className="link-col-check">
                  <label className="checkbox-wrapper">
                    <input
                      type="checkbox"
                      checked={selected.has(link.id)}
                      onChange={() => toggleSelect(link.id)}
                    />
                  </label>
                </div>
                <div className="link-col-favicon">
                  <img
                    src={faviconUrl}
                    alt=""
                    className="link-favicon"
                    draggable="false"
                    onError={(e) => { e.target.onerror = null; e.target.src = "/browser-icon.svg"; }}
                  />
                </div>
                <div className="link-info">
                  <div className="link-title">
                    <Link to={`/links/${link.id}/edit`} className="link-title-link" title="Edit link">
                      {link.title || link.short_code}
                    </Link>
                    {link.status === "scheduled" && <span className="badge-scheduled" data-tooltip={`Activates ${formatDateTimeWithTz(link.scheduled_at)}`}>Scheduled</span>}
                    {link.status === "expired" && <span className="badge-expired">Expired</span>}
                    {!link.is_active && link.status !== "scheduled" && link.status !== "expired" && <span className="badge-inactive">Inactive</span>}
                    {link.has_password && <span className="badge-password" data-tooltip="Password protected"><FiLock size={10} /></span>}
                    {link.expires_at && <span className="badge-expires" data-tooltip={`${link.status === "expired" ? "Expired on" : "Expires"} ${formatDateTimeWithTz(link.expires_at)}`}><FiClock size={10} /></span>}
                    {link.mobile_url && <span className="badge-mobile" data-tooltip="Device targeting"><FiSmartphone size={10} /></span>}
                  </div>
                  <a href={link.short_url} target="_blank" rel="noopener noreferrer" className="link-short">
                    {link.short_url} <FiExternalLink size={12} />
                  </a>
                  <div className="link-original" title={link.original_url}>
                    {link.original_url}
                  </div>
                  <div className="link-meta">
                    <span className="link-clicks">{link.click_count} clicks</span>
                    <span className="link-date">
                      {formatDate(link.created_at)}
                    </span>
                    {linkTags.length > 0 && (
                      <span className="link-tags-inline">
                        {linkTags.map((t) => (
                          <span key={t} className="tag-chip-sm" style={{ backgroundColor: getTagColor(t) + "18", color: getTagColor(t), borderColor: getTagColor(t) + "30" }}>
                            {t}
                          </span>
                        ))}
                      </span>
                    )}
                    {link.campaign_id && campaignMap[link.campaign_id] && (
                      <span
                        className="campaign-badge"
                        style={{
                          backgroundColor: (campaignMap[link.campaign_id].color || "#2383e2") + "14",
                          color: campaignMap[link.campaign_id].color || "#2383e2",
                        }}
                        data-tooltip={`Campaign: ${campaignMap[link.campaign_id].name}`}
                      >
                        <span
                          className="campaign-badge-dot"
                          style={{ backgroundColor: campaignMap[link.campaign_id].color || "#2383e2" }}
                        />
                        {campaignMap[link.campaign_id].name}
                      </span>
                    )}
                  </div>
                </div>

                <div className="link-actions">
                  <button onClick={() => navigate(`/analytics/${link.id || link.short_code}`)} className="btn-icon" data-tooltip="Analytics">
                    <FiBarChart2 />
                  </button>
                  <button onClick={() => setQrLink(link)} className="btn-icon" data-tooltip="QR Code">
                    <FiGrid />
                  </button>
                  <button onClick={() => copyUrl(link)} className="btn-icon" data-tooltip={copiedId === link.id ? "Copied!" : "Copy URL"}>
                    {copiedId === link.id ? <FiCheck /> : <FiCopy />}
                  </button>
                  <button onClick={() => cloneLink(link)} className="btn-icon" data-tooltip="Clone" disabled={cloningId === link.id}>
                    {cloningId === link.id ? <span className="spinner-sm dark" /> : <FiLayers />}
                  </button>
                  <button onClick={() => setEditLink(link)} className="btn-icon" data-tooltip="Edit">
                    <FiEdit2 />
                  </button>
                  <button onClick={() => toggleActive(link)} className="btn-icon" data-tooltip={link.is_active ? "Deactivate" : "Activate"} disabled={togglingId === link.id}>
                    {togglingId === link.id ? <span className="spinner-sm dark" /> : link.is_active ? <FiToggleRight /> : <FiToggleLeft />}
                  </button>
                  <button onClick={() => setDeleteTarget(link)} className="btn-icon btn-danger" data-tooltip="Delete">
                    <FiTrash2 />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {deleteTarget && (
        <ConfirmDialog
          title="Delete this link?"
          message={`"${deleteTarget.title || deleteTarget.short_code}" will be permanently deleted along with all its analytics data.`}
          confirmLabel="Delete"
          cancelLabel="Cancel"
          variant="danger"
          onConfirm={handleDeleteConfirm}
          onCancel={() => setDeleteTarget(null)}
        />
      )}

      {qrLink && <QRCodeModal link={qrLink} onClose={() => setQrLink(null)} />}
      {editLink && (
        <EditLinkModal
          link={editLink}
          onClose={() => setEditLink(null)}
          onUpdated={handleEditUpdated}
        />
      )}
      {showBulkEdit && (
        <BulkEditModal
          campaigns={Object.values(campaignMap)}
          selectedCount={selected.size}
          loading={bulkLoading}
          onClose={() => setShowBulkEdit(false)}
          onSubmit={handleBulkUpdate}
        />
      )}
    </>
  );
}
